<template>
  <div
    class="gnavi-course-info"
    :class="{
      small: small,
      large: large,
    }"
  >
    <div class="course">
      <h1 class="name">{{ course.name }}</h1>
      <img v-if="course.image" class="image" :src="course.image" />
      <div v-else class="image no-image"><TableSetIcon /></div>
      <div class="aside">
        <div v-if="![1, 6].includes(course.kind)" class="label">
          <div v-if="[3, 4, 5].includes(course.kind)" class="kind">飲み放題</div>
          <div v-if="[2, 4].includes(course.kind)" class="kind">食べ放題</div>
        </div>
        <div class="persons"><PersonIcon />{{ persons }}</div>
        <div v-if="course.menu.count" class="menus">
          <KnifeForkIcon />
          {{ course.menu.count }}品
        </div>
      </div>
      <div :class="['desc', { clamp: large }]">{{ description }}</div>
      <div v-if="course.price && !onlySeat" class="price">
        <template v-if="course.price.fixed && course.price.fixedTaxIn">
          <div class="fixed" :class="classes">
            {{ `${$n(course.price.fixed)}円` }}
          </div>
          <div class="tax" :class="classes">
            {{ `（${$t('tax[1]')}${$n(course.price.fixedTaxIn)}円）` }}
          </div>
        </template>
        <template v-else>
          <div class="fixed" :class="classes">
            {{ `${$n(course.price.fixed || course.price.fixedTaxIn)}円` }}
          </div>
          <div class="tax" :class="classes">
            {{ course.price.fixedTaxIn ? `（${$t('tax[1]')}）` : `（${$t('tax[2]')}）` }}
          </div>
        </template>
        <div v-if="hasDiscounted && !small" class="sp-set-new-line"></div>
        <template v-if="hasDiscounted">
          <div class="arrow"></div>
          <template v-if="course.price.discounted && course.price.discountedTaxIn">
            <div class="discounted">
              {{ `${$n(course.price.discounted)}円` }}
            </div>
            <div class="tax">
              {{ `（${$t('tax[1]')}${$n(course.price.discountedTaxIn)}円）` }}
            </div>
          </template>
          <template v-else>
            <div class="discounted">
              {{ `${$n(course.price.discounted || course.price.discountedTaxIn)}円` }}
            </div>
            <div class="tax">
              {{ course.price.discountedTaxIn ? `（${$t('tax[1]')}）` : `（${$t('tax[2]')}）` }}
            </div>
          </template>
        </template>
        <div class="per_people">{{ `/ ${$t('reserve/gnaviCourse/perPeople')}` }}</div>
      </div>
      <div v-else class="price">席のみのご予約になります。<br />お食事などを、当日ご注文いただきご精算ください。</div>
    </div>
  </div>
</template>

<script>
import KnifeForkIcon from '@/components/icons/knifeFork'
import PersonIcon from '@/components/icons/person'
import TableSetIcon from '@/components/icons/tableSet'
import { toEllipsis } from '@/utils/string'

export default {
  name: 'GnaviCourseInfo',
  components: {
    KnifeForkIcon,
    PersonIcon,
    TableSetIcon,
  },
  props: {
    course: { type: Object, required: true },
    small: { type: Boolean, default: false },
    large: { type: Boolean, default: false },
  },
  computed: {
    classes() {
      return {
        'has-discounted': this.hasDiscounted,
      }
    },
    hasDiscounted() {
      return this.course.price.discounted || this.course.price.discountedTaxIn
    },
    onlySeat() {
      return !!this.course.kind && this.course.kind === 6
    },
    persons() {
      const { min, max } = this.course.persons
      if (min === max) {
        return `${min}名様`
      } else if (max == null) {
        return `${min}名様〜`
      } else {
        return `${min}〜${max}名様`
      }
    },
    description() {
      return this.large ? toEllipsis(this.course.description, 150) : this.course.description
    },
  },
}
</script>

<style lang="postcss" scoped>
/* autoprefixer grid: autoplace */
.gnavi-course-info {
  display: flex;
  flex-direction: column;
  line-height: initial;

  & .course {
    background: var(--white);
    cursor: default;
    display: grid;
    grid-gap: 12px 8px;
    grid-template:
      'image name' max-content
      'image aside' max-content
      'image desc' max-content
      'image price' max-content /
      88px 1fr;

    @media screen and (max-width: 768px) {
      grid-gap: 8px 16px;
      grid-template:
        'image name' auto
        'image aside' auto
        'image price' auto
        'desc desc' auto /
        104px 1fr;
    }

    & .image {
      grid-area: image;
    }
    & .name {
      grid-area: name;
    }
    & .aside {
      grid-area: aside;
    }
    & .price {
      grid-area: price;
    }
    & .desc {
      grid-area: desc;
    }
  }

  & .name {
    color: var(--very-dark-gray-1);
    font-size: 20px;
    font-weight: 600;

    @media screen and (max-width: 768px) {
      font-size: 14px;
    }
  }

  & .image {
    display: inherit;
    height: 88px;
    object-fit: contain;
    width: 88px;

    @media screen and (max-width: 768px) {
      height: 104px;
      width: 104px;
    }
  }

  & .no-image {
    align-items: center;
    background: var(--very-light-gray-2);
    display: flex;
    justify-content: center;

    & > svg {
      fill: var(--light-gray);
      height: 80px;
      width: 80px;

      @media screen and (max-width: 768px) {
        height: 40px;
        width: 40px;
      }
    }
  }

  & .aside {
    align-items: center;
    display: flex;
    flex-wrap: wrap;

    & .label {
      @media screen and (max-width: 768px) {
        margin-bottom: 4px;
        width: 100%;
      }
    }

    & .kind {
      border: 1px solid #0278b3;
      border-radius: 2px;
      color: #0278b3;
      display: inline-block;
      font-size: 12px;
      height: 20px;
      margin-right: 4px;
      text-align: center;
      width: 60px;
    }

    & .persons,
    & .menus {
      align-items: center;
      display: inline-flex;
      font-size: 12px;
      margin-right: 8px;

      @media screen and (max-width: 768px) {
        padding: 0;
      }
    }

    & svg {
      height: 20px;
      margin-right: 4px;
      width: 20px;

      @media screen and (max-width: 768px) {
        height: 16px;
        width: 16px;
      }
    }

    @media screen and (max-width: 768px) {
      font-size: 13px;
      padding: 0;
    }
  }

  & .desc {
    white-space: pre-line;
  }

  & .price {
    align-items: baseline;
    display: flex;
    flex-wrap: wrap;

    & > .arrow {
      margin: 0px 4px 0px 4px;

      &::after {
        content: '→';
      }
    }

    & > .fixed,
    & > .discounted {
      color: var(--bright-red-1);
      font-size: 20px;
      font-weight: 600;

      @media screen and (max-width: 768px) {
        font-size: 16px;
      }
    }

    & .has-discounted {
      color: var(--text-color);
      font-size: 12px;
      font-weight: 300;
      text-decoration: line-through;

      @media screen and (max-width: 768px) {
        font-size: 10px;
      }
    }

    & .tax,
    & .per_people {
      font-size: 12px;

      @media screen and (max-width: 768px) {
        font-size: 10px;
      }
    }
  }

  & .sp-set-new-line {
    display: none;
    width: 100%;

    @media screen and (max-width: 768px) {
      display: initial;
    }
  }
}

.gnavi-course-info.large {
  & .course {
    grid-gap: 8px 16px;
    grid-template:
      'image name' max-content
      'image aside' max-content
      'image desc' auto
      'image price' max-content /
      168px 1fr;

    @media screen and (max-width: 768px) {
      grid-gap: 4px 16px;
      grid-template:
        'image name' auto
        'image aside' auto
        'image price' auto
        'desc desc' max-content /
        88px 1fr;
    }
  }

  & .image {
    height: 168px;
    width: 168px;

    @media screen and (max-width: 768px) {
      height: 88px;
      width: 88px;
    }
  }

  & .desc {
    white-space: normal;
  }
}

.gnavi-course-info.small {
  & .course {
    grid-gap: 12px 8px;
    grid-template:
      'image name' 17px
      'image aside' 20px
      'image price' max-content /
      88px 1fr;

    @media screen and (max-width: 768px) {
      grid-gap: 8px;
      grid-template:
        'name' auto
        'aside' auto
        'price' auto /
        1fr;
      padding-top: 0px;
    }
  }

  & .name {
    font-size: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @media screen and (max-width: 768px) {
      overflow: initial;
      text-overflow: initial;
      white-space: initial;
    }
  }

  & .image {
    height: 88px;
    width: 88px;

    @media screen and (max-width: 768px) {
      display: none;
    }
  }

  & .label {
    @media screen and (max-width: 768px) {
      display: none;
    }
  }

  & .desc {
    display: none;
  }
}

.clamp {
  /*! autoprefixer: off */
  -webkit-box-orient: vertical;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  overflow: hidden;
}
</style>
